import * as React from 'react'
import {observer} from 'mobx-react-lite'
import {Dialog, DialogActions, DialogContent, Grid} from '@mui/material'
import {makeOcStyles} from '@oc/styles'
import {xlBreakpoint, lgBreakpoint, mdBreakpoint, smBreakpoint} from './OcGenericDialogInterfaces'
import {OcGenericDialogState} from './OcGenericEditState'
import {getValueHelperFunction, getDialogStateInputFields} from './OcGenericDialogFunctions'
import {OcGenericDialogProps, OcGenericDialogStatus} from './OcGenericDialogInterfacesExport'
import {OcGenericDialogTitleComponent} from './OcGenericDialogTitle'
import {CustomBreakpoints} from '../CustomBreakpoints'
import {OcGenericDialogGroupComponent} from './OcGenericDialogGroupComponent'
import {OcButton} from '../..'
import {OcGenericDialogStateContextProvider, useOcGenericDialogStateContext} from './OcGenericDialogContext'

interface OcGenericDialogContentProps<SourceType> extends Pick<OcGenericDialogProps<SourceType>, 'dataGroups'> {
    dialogState: OcGenericDialogState<SourceType>
    getValueCallback: (group: string, field: string) => any
}

interface OcGenericDialogActionsProps<SourceType> extends Pick<OcGenericDialogProps<SourceType>, 'actions'> {
    dialogState: OcGenericDialogState<SourceType>
    onClose: (action: string) => void
}

const useStyles = makeOcStyles(theme => ({
    dialogRoot: {
        minWidth: '95vw',
        maxWidth: '95vw',
        height: '95vh'
    },
    dialogContent: {
        padding: 0,

        borderTop: `1px solid ${theme.palette.divider}`
    },
    button: {
        minWidth: '132px'
    }
}))

function _OcGenericDialogContent<SourceType>(props: OcGenericDialogContentProps<SourceType>) {
    const {dataGroups, getValueCallback, dialogState} = props
    const allowedGroups = dialogState.allowedGroups

    return (
        <Grid container>
            {Object.entries(dataGroups)
                .filter(group => group[1] && allowedGroups.includes(group[0]))
                .sort((group1, group2) => group1[1].index - group2[1].index)
                .map(group => (
                    <OcGenericDialogGroupComponent
                        {...group[1]}
                        key={`oc-generic-dialog-group-${group[0]}-component`}
                        dialogState={dialogState}
                        getValueCallback={getValueCallback}
                        groupName={group[0]}
                    />
                ))}
        </Grid>
    )
}

const OcGenericDialogContent = observer(_OcGenericDialogContent)

function _OcGenericDialogActions<SourceType>(props: OcGenericDialogActionsProps<SourceType>) {
    const {dialogState, onClose, actions} = props
    const {classes, cx} = useStyles()

    return actions ? (
        <DialogActions>
            {Object.entries(actions).map(([actionName, actionProps]) => {
                const {disableOnError, disableWithoutChange, buttonBaseProps, label} = actionProps
                return (
                    <OcButton
                        {...buttonBaseProps}
                        key={`oc-generic-dialog-action-${actionName}`}
                        onClick={() => onClose(actionName)}
                        disabled={
                            (disableWithoutChange && !dialogState.isChanged) ||
                            (disableOnError && dialogState.hasError) ||
                            buttonBaseProps?.disabled
                        }
                        className={
                            buttonBaseProps?.className ? cx(buttonBaseProps.className, classes.button) : classes.button
                        }
                    >
                        {label}
                    </OcButton>
                )
            })}
        </DialogActions>
    ) : null
}

const OcGenericDialogActions = observer(_OcGenericDialogActions)

function OcGenericDialogData<SourceType>(props: OcGenericDialogProps<SourceType>) {
    const {data, dataGroups, onClose, actions, defaultDialogTitleProps, customDialogTitle, ...restProps} = props
    const {classes} = useStyles()

    const dialogState = useOcGenericDialogStateContext<SourceType>()

    const getValueCallback = React.useCallback(
        (group: string, field: string) => getValueHelperFunction<SourceType>(group, field, data || [], dataGroups),
        [dataGroups, data]
    )

    const onCloseCallback = React.useCallback(
        async (actionKey: string) => {
            const selectedAction = actions ? actions[actionKey] : undefined
            if (selectedAction) {
                if (selectedAction.validation) {
                    if (dialogState.bulkEdit) {
                        dialogState.validateChangedData()
                    } else {
                        dialogState.validateAllData()
                    }
                }

                if (
                    !dialogState.hasError ||
                    (dialogState.hasError && selectedAction.closeWithErrors) ||
                    (selectedAction.skipValidationForDeleted && dialogState.status === OcGenericDialogStatus.Deleted)
                ) {
                    if (selectedAction.applyChanges) {
                        dialogState.applyChanges()
                    }

                    try {
                        await onClose(actionKey, dialogState.dialogOutput, dialogState.status)
                    } catch (err) {
                        console.error(err)
                        return
                    }
                    dialogState.resetState([])
                }
            }
        },
        [onClose, dialogState, actions]
    )

    const onEnterCallback = React.useCallback(() => {
        const fieldInputs = getDialogStateInputFields<SourceType>(dataGroups, getValueCallback)
        dialogState.resetState(fieldInputs, defaultDialogTitleProps?.initialStatus, ...(data || []))
    }, [dialogState, data, getValueCallback, dataGroups, defaultDialogTitleProps])

    return (
        <Dialog
            {...restProps}
            TransitionProps={{
                onEnter: onEnterCallback
            }}
            classes={{paper: classes.dialogRoot}}
        >
            <CustomBreakpoints
                breakpoints={{
                    xl: xlBreakpoint,
                    lg: lgBreakpoint,
                    md: mdBreakpoint,
                    sm: smBreakpoint
                }}
            >
                {customDialogTitle ? (
                    customDialogTitle
                ) : defaultDialogTitleProps ? (
                    <OcGenericDialogTitleComponent {...defaultDialogTitleProps} dialogState={dialogState} />
                ) : undefined}

                <DialogContent className={classes.dialogContent}>
                    {dialogState ? (
                        <OcGenericDialogContent
                            dataGroups={dataGroups}
                            dialogState={dialogState}
                            getValueCallback={getValueCallback}
                        />
                    ) : undefined}
                </DialogContent>
                <OcGenericDialogActions dialogState={dialogState} onClose={onCloseCallback} actions={actions} />
            </CustomBreakpoints>
        </Dialog>
    )
}

function _OcGenericDialog<SourceType>(props: OcGenericDialogProps<SourceType>) {
    return (
        <OcGenericDialogStateContextProvider>
            <OcGenericDialogData {...props} />
        </OcGenericDialogStateContextProvider>
    )
}
export const OcGenericDialog = observer(_OcGenericDialog)
